/** @format */

import {
	selectAllProductSkus,
	selectAllProductStyle,
	selectKillswitch,
	selectShouldShowAlert,
	selectSkusWithInventory
} from '@pdp/data-access/state';

import { AsyncPipe, isPlatformBrowser, NgClass } from '@angular/common';
import { Component, DestroyRef, Inject, input, OnInit, PLATFORM_ID } from '@angular/core';
import { EmitEvent, EventBusService, Events, UtilityService } from '@shared/data-access/services';
import { Store } from '@ngrx/store';

import { first, map, take } from 'rxjs/operators';
import { ActivatedRoute } from '@angular/router';
import {
	AttributeOption,
	AttributesAnalyticsService,
	attributeSelected,
	AttributesTrueFitService,
	selectAttributes,
	selectFirstSizeAttributeName,
	selectSelectedAttributeOptions,
	selectShouldShowAlternativeInStockRecs
} from '@pdp/attributes/data-access';
import { Observable } from 'rxjs';
import {
	Chain,
	ProductSku,
	ProductStyle,
	QueryParam,
	SkuAttribute
} from '@shared/data-access/interfaces';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PriceComponent, SizeChartComponent } from '@pdp/components';
import { AttributeLabelComponent } from '../attribute-label/attribute-label.component';
import { ColorAttributesComponent } from '../color-attributes/color-attributes.component';
import { ColorAttributesPanelComponent } from '../color-attributes-panel/color-attributes-panel.component';
import { TextColorAttributeComponent } from '../text-color-attributes/text-color-attribute.component';
import { SelectorAttributesComponent } from '../selector-attributes/selector-attributes.component';
import { SizeAttributesPanelComponent } from '../size-attributes-panel/size-attributes-panel.component';
import { HmfAlertBannerComponent } from '@dsg-tech/homefield-angular';
import { ProductRecommendationsComponent } from '@shared/components';
import { ConfigAttributesPanelComponent } from '../config-attributes-panel/config-attributes-panel.component';
import { PersonalizationComponent } from '../personalization/personalization.component';

@Component({
	selector: 'pdp-attributes-components-attributes',
	templateUrl: './attributes.component.html',
	styleUrl: './attributes.component.scss',
	imports: [
		NgClass,
		PriceComponent,
		AttributeLabelComponent,
		ColorAttributesComponent,
		ColorAttributesPanelComponent,
		TextColorAttributeComponent,
		SelectorAttributesComponent,
		SizeAttributesPanelComponent,
		SizeChartComponent,
		HmfAlertBannerComponent,
		ProductRecommendationsComponent,
		ConfigAttributesPanelComponent,
		PersonalizationComponent,
		AsyncPipe
	]
})
export class AttributesComponent implements OnInit {
	readonly alternativeInStockRecsEnabled = input<boolean>();
	readonly colorAttributeBuckets = input<SkuAttribute[]>([]);
	readonly colorSelectionPanelEnabled = input<boolean>();
	readonly customSeoUrl = input<string>('');
	readonly disableSkuLevelPricing = input<boolean>(false);
	readonly dropdownAttributesEnabled = input<boolean>();
	readonly isApparelOrFootwear = input<boolean>();
	readonly isLoadingStateRecsEnabled = input<boolean>();
	readonly nonColorQueryParams = input<QueryParam[]>();
	readonly sizeSelectionPanelEnabled = input<boolean>();
	readonly skuIdQueryParam = input<string>();
	readonly isMobile = input<boolean>();

	attributes$ = this.store.select(selectAttributes);
	colorQueryParam: string;
	isFootwear: boolean;
	isPersonalizationProduct: boolean;
	product$ = this.store.select(selectAllProductStyle);
	productSkus = this.store.selectSignal(selectAllProductSkus);
	selectedAttributes = this.store.selectSignal(selectSelectedAttributeOptions);
	selectedAttributes$ = this.store.select(selectSelectedAttributeOptions);
	selectedBucketIndex: number;
	selectShouldShowAlternativeInStockRecs$ = this.store.select(
		selectShouldShowAlternativeInStockRecs
	);
	/** @description Determines whether to show alert box for low stock. */
	shouldShowAlert$ = this.store.select(selectShouldShowAlert);
	shouldShowAlternativeInStockRecs: boolean;
	sizeChartAttribute = this.store.selectSignal(selectFirstSizeAttributeName);
	truefitEnabled = this.store.selectSignal(selectKillswitch('truefitEnabled'));
	trueFitSelectedColor: string;
	product: ProductStyle | Record<string, never>;

	skusWithInventory = this.store.selectSignal(selectSkusWithInventory);
	skusWithInventoryInitialized$ = this.store
		.select(selectSkusWithInventory)
		.pipe(first((value) => value.isDataInitialized));

	constructor(
		@Inject(PLATFORM_ID) private platformId: object,
		private attributesAnalyticsService: AttributesAnalyticsService,
		private attributesTrueFitService: AttributesTrueFitService,
		private eventBusService: EventBusService,
		private store: Store,
		private utilityService: UtilityService,
		public chain: Chain,
		public route: ActivatedRoute,
		private readonly destroyRef: DestroyRef
	) {}

	ngOnInit(): void {
		this.product$.pipe(takeUntilDestroyed(this.destroyRef), take(1)).subscribe((data) => {
			this.product = data;
			this.retrieveProductDetails();
		});
	}

	retrieveProductDetails() {
		if (isPlatformBrowser(this.platformId) && this.truefitEnabled()) {
			this.getSelectedColor().subscribe((trueFitSelectedColor) => {
				this.trueFitSelectedColor = trueFitSelectedColor;
				this.attributesTrueFitService.loadTruefitScriptIfEnabled(
					this.attributes$,
					this.trueFitSelectedColor,
					this.product.partNumber,
					this.productSkus()
				);
			});
		}

		this.selectShouldShowAlternativeInStockRecs$
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((shouldShowAlternativeInStockRecs) => {
				this.shouldShowAlternativeInStockRecs = shouldShowAlternativeInStockRecs;

				// Start Intialize Alternative InStock feature - SEE DOC IMAGE Inside attributes/docs/screenshots/alternative-instock
				if (
					this.alternativeInStockRecsEnabled() &&
					this.isApparelOrFootwear() &&
					this.shouldShowAlternativeInStockRecs
				) {
					/** @description call method to send sku data to analytics team to create certona for alternative InStock */
					this.fireAlternativeInStockAnalyticsEventToGetCertonaData(
						this.selectedAttributes()
					);
				}
			});

		// Don't attempt to resolve sku on load until skus with inventory have been initialized
		this.skusWithInventoryInitialized$.subscribe(() => {
			this.resolveSkuWithSkuIdParam();

			if (!this.colorSelectionPanelEnabled()) {
				this.selectColorQueryParamAttributeValue();
			}

			if (isPlatformBrowser(this.platformId)) {
				this.selectNonQueryParamAttributeValue();
			}
		});

		this.isPersonalizationProduct = this.product.isPersonalizable;
	}

	/**
	 * @description Dispatches action event to attribute reducer,
	 * to update the store with the current selected attribute.
	 **/
	selectedAttributeOnClick(selectedAttribute: AttributeOption) {
		this.selectedAttributes$.pipe(take(1)).subscribe(async (attributes: AttributeOption[]) => {
			const attributeAlreadySelected = attributes.find(
				(attribute) =>
					attribute.name === selectedAttribute.name &&
					attribute.value === selectedAttribute.value
			);

			if (!attributeAlreadySelected) {
				if (selectedAttribute.name.toLowerCase() === 'color') {
					const selectedColor = selectedAttribute.value;
					const numColorAttributeBuckets = this.colorAttributeBuckets().length;
					for (let index = 0; index < numColorAttributeBuckets; index++) {
						const bucket = this.colorAttributeBuckets()[index];
						if (bucket.bucketColors.includes(selectedColor)) {
							this.selectedBucketIndex = index;
							break; // Exit the loop once a match is found
						}
					}
				}

				await this.utilityService.yieldTask(() => {
					this.store.dispatch(
						attributeSelected({
							selectedAttribute,
							allProductSkus: this.skusWithInventory().skus,
							alternativeInStockRecsEnabled:
								this.alternativeInStockRecsEnabled() && this.isApparelOrFootwear()
						})
					);
					this.attributesAnalyticsService.fireAttributeSelectedAnalyticsEvent(
						selectedAttribute
					);
				});

				this.eventBusService.emit(
					new EmitEvent(Events.AttributeClickEvent, this.productSkus())
				);
			}
		});
	}

	/**
	 * @description Executes the attribute selection logic for default values coming from config panel.
	 * @param selectedAttributes
	 */
	onConfigPanelAttributesSelected(selectedAttributes: AttributeOption[] | AttributeOption) {
		if (Array.isArray(selectedAttributes)) {
			selectedAttributes.forEach((selectedAttribute) => {
				this.selectedAttributeOnClick(selectedAttribute);
			});
		} else {
			this.selectedAttributeOnClick(selectedAttributes);
		}
	}

	/**
	 * @description select non color attribute that is passed as a param in url
	 * @example ?color=black
	 **/
	selectColorQueryParamAttributeValue(): void {
		if (this.route.queryParams) {
			this.route.queryParams.subscribe((params) => {
				// check if the multiple query string values exist
				if (Array.isArray(params['color'])) {
					this.colorQueryParam = params['color'].at(-1);
				} else if (params['color']) {
					this.colorQueryParam = params['color'];
				}
			});
		}

		if (this.colorQueryParam) {
			const color = {
				name: 'Color',
				value: this.colorQueryParam
			};
			this.selectedAttributeOnClick(color);
		}
	}

	/**
	 * @description select non color attribute that is passed as a param in url
	 * @example ?size=m
	 **/
	selectNonQueryParamAttributeValue(): void {
		if (this.nonColorQueryParams()) {
			this.nonColorQueryParams().forEach((param) => {
				this.checkDescAttributesAvailable(param);
			});
		}
	}

	/**
	 * @description selects Desc Attribute if present in the url
	 */
	checkDescAttributesAvailable(attr) {
		const attrValue = attr.value.includes(',') ? attr.value.split(',')[0] : attr.value;
		const attributeMatch = this.product.definingAttributes.find(
			(attribute) =>
				attribute.name.replace(/\s/g, '').replace(/\//g, '-').toLowerCase() ===
				attr.name.toLowerCase()
		);

		if (!attributeMatch) {
			return;
		}
		const attributeList = attributeMatch.value.split('|');
		attributeList.forEach((comparisonValue) => {
			if (comparisonValue.toLowerCase() === attrValue.toLowerCase()) {
				const attribute = {
					name: attributeMatch.name,
					value: comparisonValue
				};
				this.selectedAttributeOnClick(attribute);
			}
		});
	}

	/**
	 * @description make attribute option selections for a sku Id passed as a param so it will sku resolve and update the UI.
	 * @example ?sku=22524289
	 **/
	resolveSkuWithSkuIdParam(): void {
		if (this.skuIdQueryParam()) {
			const matchingSkuData = this.productSkus().find(
				(sku: ProductSku) => this.skuIdQueryParam() === sku.partNumber
			);

			if (matchingSkuData && matchingSkuData.definingAttributes) {
				matchingSkuData.definingAttributes.forEach((defAttr) => {
					const attr = {
						name: defAttr.name,
						value: defAttr.value
					};
					this.selectedAttributeOnClick(attr);
				});
			}
		}
	}

	/**
	 * @description Retrieves the selected color options from the store.
	 * @return Returns the selected color if available, else return an empty string.
	 **/
	private getSelectedColor(): Observable<string> {
		return this.selectedAttributes$.pipe(
			take(5),
			map((firstFiveSelectedAttributes) => {
				const colorAttribute = firstFiveSelectedAttributes.find(
					(attribute) => attribute.name === 'Color'
				);
				return colorAttribute?.value ?? '';
			})
		);
	}

	/**
	 * @description
	 * determines if attribute name is size
	 * @param attributeName
	 */
	public isSize(attributeName: string): boolean {
		return attributeName.toLowerCase().includes('size');
	}

	public displayAlternativeInStock(defAttribute: AttributeOption, isLastAttributeGroup: boolean) {
		// If softline has no size attribute, show alternative in stock recs after the last attribute group
		const isLastOnSoftlineWithoutSize =
			isLastAttributeGroup && this.isApparelOrFootwear() && !this.sizeChartAttribute();
		return (
			this.shouldShowAlternativeInStockRecs &&
			this.alternativeInStockRecsEnabled() &&
			(defAttribute.name === 'Shoe Size' ||
				defAttribute.name.toLowerCase() === 'size' ||
				isLastOnSoftlineWithoutSize)
		);
	}

	public fireAlternativeInStockAnalyticsEventToGetCertonaData(
		selectedDefiningAttributes: AttributeOption[]
	) {
		const alternativeInStockSelectedAttributes = [];
		alternativeInStockSelectedAttributes.push({ ecode: this.product.partNumber });
		// formatting selected attributes to be sent to certona
		for (const selectedDefAttr in selectedDefiningAttributes) {
			// eslint-disable-next-line no-prototype-builtins
			if (selectedDefiningAttributes.hasOwnProperty(selectedDefAttr)) {
				const selectedAttr = {
					name: selectedDefiningAttributes[selectedDefAttr].name,
					value: selectedDefiningAttributes[selectedDefAttr].value
				};
				alternativeInStockSelectedAttributes.push(selectedAttr);
			}
		}

		this.attributesAnalyticsService.triggerAnalyticsEvent(
			'AlternativeInStockProduct',
			alternativeInStockSelectedAttributes
		);
	}
}
